import {
  AccountInfo,
  AuthenticationResult,
  ClientConfigurationError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { MSAL_CONFIG, getMsalEntraConfig, environments } from './msal-config';
import { Observable, of, from } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';

import { API_HEADER_KEYS, LoginSource } from '../constants/global.constant';
import { CommonUtility } from '../utils/commonUtils';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Auth0TokenService } from '../services/auth0-service/auth0-service';
import { bypassURLSet, endPoints } from '../constants/endpoints';
import { LoginService } from '../services/login-service/login.service';

@Injectable()
export class MsalInterceptor implements HttpInterceptor {
  private initialized: boolean = false;
  private cachedLoginHint: string | null = null;
  private hintExpiry: number | null = null;
  private accountDiscovery$: Observable<AccountInfo | null> | null = null;

  constructor(
    private readonly msalService: MsalService,
    private readonly auth0TokenService: Auth0TokenService,
    private readonly loginService: LoginService,
    private readonly http: HttpClient,
  ) {}

  /**
   * Check and initialize MSAL service instance
   * @returns Promise that resolves when MSAL is initialized
   */
  private initializeMsal(): Promise<void> {
    if (!this.initialized) {
      return this.msalService.instance
        .initialize()
        .then(() => {
          this.initialized = true;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
    return Promise.resolve();
  }

  /**
   * Interceptor for HTTP requests
   * @param req request
   * @param next handler
   * @returns request
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.auth0TokenService.loginSource === LoginSource.Auth0) {
      return next.handle(req);
    }
    // do nothing if url is in bypass
    if (bypassURLSet.has(req.url)) {
      return next.handle(req);
    }
    const isContentJSON = req.headers.get('isContentJSON') === 'true';
    const env =
      environments.get(
        CommonUtility.getWindowLocationProperty('hostname').split('.')[0],
      ) || 'upsdev';
    if (isContentJSON) {
      return next.handle(req);
    }

    const isV2Login: boolean = this.auth0TokenService.appConfig().v2Login;
    const msalConfig: any = isV2Login ? getMsalEntraConfig() : MSAL_CONFIG[env];

    // Prefer the explicitly selected account and only use an unambiguous authority match.
    let activeAccounts: AccountInfo | null =
      this.msalService?.instance.getActiveAccount();
    if (
      activeAccounts &&
      !this.isAccountCompatible(activeAccounts, msalConfig)
    ) {
      activeAccounts = null;
    }
    if (!activeAccounts) {
      const accounts: AccountInfo[] = this.msalService?.instance
        .getAllAccounts()
        ?.filter((account: AccountInfo) =>
          this.isAccountCompatible(account, msalConfig),
        );
      activeAccounts = accounts?.length === 1 ? accounts[0] : null;
    }
    if (!activeAccounts) {
      return this.discoverAccount(msalConfig).pipe(
        switchMap((account: AccountInfo | null) =>
          account
            ? this.acquireTokenAndSetheader(
                msalConfig,
                account,
                isV2Login,
                req,
                next,
              )
            : next.handle(req),
        ),
      );
    } else {
      return this.acquireTokenAndSetheader(
        msalConfig,
        activeAccounts,
        isV2Login,
        req,
        next,
      );
    }
  }

  acquireTokenAndSetheader(
    msalConfig: any,
    activeAccounts: AccountInfo,
    isV2Login: boolean,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    // Post login hint to BFF for cross-subdomain SSO (throttled to avoid excessive calls)
    this.setLoginHint(activeAccounts.username);

    // Ensure MSAL is initialized before acquiring token
    return from(this.initializeMsal()).pipe(
      catchError(() => {
        return of(null);
      }),
      switchMap((initResult) => {
        // If initialization failed, just pass through the request
        if (initResult === null) {
          return next.handle(req);
        }
        return this.msalService
          .acquireTokenSilent({
            scopes: msalConfig.resourceScopes,
            account: activeAccounts,
            authority: msalConfig.authorityUrl,
          })
          .pipe(
            take(1),
            catchError((error: ClientConfigurationError) => {
              if (error.errorCode === 'authority_mismatch') {
                this.loginService.logout();
              }

              if (
                error instanceof InteractionRequiredAuthError &&
                this.loginService.beginAuthRecovery()
              ) {
                const returnto: URL = new URL(CommonUtility.getLoginReturnTo());
                this.msalService.loginRedirect({
                  // scopes property is mandatory in RedirectRequest, so gave with []
                  scopes: [],
                  prompt: msalConfig.prompt,
                  state: CommonUtility.getMsalRedirectUrl(returnto),
                });
              }

              if (!CommonUtility.isProdEnv()) {
                console.error(error);
              }
              return of(null);
            }),
            switchMap((result: AuthenticationResult | null) => {
              let headers: HttpHeaders = req.headers;

              if (result?.accessToken) {
                headers = req.headers.set(
                  isV2Login
                    ? API_HEADER_KEYS.X_Authorization
                    : API_HEADER_KEYS.Authorization,
                  `Bearer ${result?.accessToken}`,
                );
              }

              const requestClone: HttpRequest<any> = req.clone({
                headers,
              });
              return next.handle(requestClone);
            }),
          );
      }),
    );
  }

  private fetchLoginHint(): Observable<string | null> {
    // Return cached hint if valid
    if (
      this.cachedLoginHint &&
      this.hintExpiry &&
      Date.now() < this.hintExpiry
    ) {
      return of(this.cachedLoginHint);
    }

    return this.http
      .get<{ value: string | null; expires: string | null }>(
        endPoints.casemgmt.symphonyMsalHint.URI,
        {
          withCredentials: true,
        },
      )
      .pipe(
        switchMap((response) => {
          // Handle expiry: set if provided, clear if explicitly null
          if (response?.expires) {
            this.hintExpiry = new Date(response.expires).getTime();
          } else if (response && response.expires === null) {
            // API explicitly returned expires: null, clear any stale expiry
            this.hintExpiry = null;
          }

          if (!response?.value) {
            // Clear cache when API returns null value to prevent using stale data
            this.cachedLoginHint = null;
            this.hintExpiry = null;
            return of(null);
          }

          this.cachedLoginHint = response.value;
          return of(this.cachedLoginHint);
        }),
        catchError((error) => {
          // Clear cache on error to allow retry on next request
          this.cachedLoginHint = null;
          this.hintExpiry = null;

          if (!CommonUtility.isProdEnv()) {
            console.warn(
              'Failed to fetch login hint from BFF (SSO will proceed without hint):',
              error,
            );
          }
          return of(null);
        }),
      );
  }

  private discoverAccount(msalConfig: any): Observable<AccountInfo | null> {
    if (!this.accountDiscovery$) {
      this.accountDiscovery$ = this.fetchLoginHint().pipe(
        switchMap((loginHint: string | null) => {
          const ssoRequest: any = { scopes: msalConfig.resourceScopes };
          if (loginHint) {
            ssoRequest.loginHint = loginHint;
          }
          return this.msalService.ssoSilent(ssoRequest).pipe(take(1));
        }),
        map((response: AuthenticationResult | null) => {
          const account = response?.account ?? null;
          if (account) {
            this.msalService.instance.setActiveAccount(account);
            this.auth0TokenService.loginSource = LoginSource.B2E;
          }
          return account;
        }),
        catchError((error) => {
          if (!CommonUtility.isProdEnv()) {
            console.log('MSAL SSO failed:', error);
          }
          return of(null);
        }),
        finalize(() => {
          this.accountDiscovery$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.accountDiscovery$;
  }

  private isAccountCompatible(account: AccountInfo, msalConfig: any): boolean {
    return account.environment === msalConfig.authorityDomain[0];
  }

  private setLoginHint(username: string): void {
    const TEN_MINS_MS = 10 * 60 * 1000;
    const now = Date.now();

    if (this.hintExpiry !== null && now < this.hintExpiry - TEN_MINS_MS) {
      return;
    }

    this.http
      .post<{ expires: string | null }>(
        endPoints.casemgmt.symphonyMsalHint.URI,
        { value: username },
        { withCredentials: true },
      )
      .pipe(
        catchError((error) => {
          if (!CommonUtility.isProdEnv()) {
            console.warn('Failed to set login hint in BFF:', error);
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response?.expires) {
          this.hintExpiry = new Date(response.expires).getTime();
        }
      });
  }
}
